import { usePermissions as useMaterialPermissions } from '../../../hooks/usePermissions';
import MaterialTransitionFields from '../../components/MaterialTransitionFields';
import { materialTransitionPayload } from '../../utils/materialTransitionPayload';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextArea,
  CustomTextField,
  DecimalTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  renderTwoLineOption,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { useAdjustArticleStock } from '../../../hooks/useArticleStocks';
import {
  ADJUST_STOCK_TEXTS as TEXTS,
  ADJUSTMENT_TYPES,
  ADJUSTMENT_TYPE_OPTIONS,
  INITIAL_ADJUST_STOCK_STATE,
} from '../constants/inventoryArticlesConstants';
import {
  buildAdjustStockPayload,
  getArticleLabel,
  getArticleStockApiErrorMessage,
  getEstimatedResultingStock,
  getWarehouseLabel,
  formatStockQuantity,
  toStockNumber,
  validateAdjustStockForm,
} from '../utils/inventoryArticlesUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';

const AdjustArticleStockDialog = ({
  open,
  onClose,
  article,
  stockRow = null,
  stocks = [],
  warehouses = [],
  warehousesByUuid = {},
  onSuccess,
  onPendingChange,
}) => {
  const { permissions: materialPermissions } = useMaterialPermissions();
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [valuation, setValuation] = useState({});
  const [formData, setFormData] = useState(INITIAL_ADJUST_STOCK_STATE);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const adjustMutation = useAdjustArticleStock();
  const isLocked = adjustMutation.isPending;

  useReportPendingState(isLocked, onPendingChange);

  const isExistingStockRow = Boolean(stockRow?.uuid_warehouse);
  const selectedWarehouseId = isExistingStockRow
    ? stockRow.uuid_warehouse
    : formData.uuid_warehouse;

  const currentStock = useMemo(() => {
    if (isExistingStockRow) {
      return toStockNumber(stockRow?.quantity_on_hand);
    }

    if (!selectedWarehouseId) {
      return 0;
    }

    const matchingStock = stocks.find(
      (stock) => stock.uuid_warehouse === selectedWarehouseId
    );

    return toStockNumber(matchingStock?.quantity_on_hand);
  }, [
    isExistingStockRow,
    selectedWarehouseId,
    stockRow?.quantity_on_hand,
    stocks,
  ]);

  const warehouseLabel = getWarehouseLabel(warehousesByUuid, selectedWarehouseId);

  const warehouseOptions = useMemo(() => toWarehouseOptions(warehouses), [warehouses]);

  const estimatedStock = useMemo(
    () => getEstimatedResultingStock(currentStock, formData.adjustmentType, formData.quantity),
    [currentStock, formData.adjustmentType, formData.quantity]
  );

  useEffect(() => {
    if (open) {
      setRequestId(crypto.randomUUID());
      setValuation({});
      setFormData({
        ...INITIAL_ADJUST_STOCK_STATE,
        uuid_warehouse: stockRow?.uuid_warehouse || '',
      });
      setErrors({});
      setErrorMessage('');
    }
  }, [open, stockRow?.uuid, stockRow?.uuid_warehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateAdjustStockForm(formData, currentStock, {
      requireWarehouse: !isExistingStockRow,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const warehouseUuid = isExistingStockRow
      ? stockRow.uuid_warehouse
      : formData.uuid_warehouse;

    if (!article?.uuid || !warehouseUuid) {
      return;
    }

    try {
      setErrorMessage('');
      const payload = buildAdjustStockPayload(article.uuid, warehouseUuid, formData);
      payload.request_id = requestId;
      payload.valuation = materialTransitionPayload(valuation);
      await adjustMutation.mutateAsync(payload);
      onSuccess?.();
      onClose();
    } catch (error) {
      setErrorMessage(
        getArticleStockApiErrorMessage(error, TEXTS.saveError)
      );
    }
  };

  if (!open) return null;

  const showTransitionValuation =
    materialPermissions.canManageMaterialValuation
    && formData.adjustmentType === ADJUSTMENT_TYPES.IN;

  return (
    <DialogContainer open={open} onClose={guardDialogClose(isLocked, onClose)} size="medium">
      <DialogTitle>{TEXTS.title}</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {showTransitionValuation && (
          <MaterialTransitionFields value={valuation} onChange={setValuation} />
        )}

        <CustomTextField
          label={TEXTS.article}
          name="article"
          value={getArticleLabel(article)}
          disabled
        />

        {isExistingStockRow ? (
          <CustomTextField
            label={TEXTS.warehouse}
            name="warehouse"
            value={warehouseLabel}
            disabled
          />
        ) : (
          <CustomAutoComplete
            label={TEXTS.warehouse}
            name="uuid_warehouse"
            value={formData.uuid_warehouse}
            options={warehouseOptions}
            renderOption={renderTwoLineOption}
            inputPlaceholder={TEXTS.warehousePlaceholder}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            onChange={handleChange}
            required
            error={Boolean(errors.uuid_warehouse)}
            helperText={errors.uuid_warehouse}
          />
        )}

        <CustomTextField
          label={TEXTS.currentStock}
          name="currentStock"
          value={formatStockQuantity(currentStock)}
          disabled
        />

        <CustomAutoComplete
          label={TEXTS.adjustmentType}
          name="adjustmentType"
          value={formData.adjustmentType}
          options={ADJUSTMENT_TYPE_OPTIONS}
          onChange={handleChange}
        />

        <DecimalTextField
          label={TEXTS.quantity}
          name="quantity"
          value={formData.quantity}
          onChange={handleChange}
          required
          error={Boolean(errors.quantity)}
          helperText={errors.quantity}
        />

        <CustomTextArea
          label={TEXTS.reason}
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          minRows={2}
          error={Boolean(errors.reason)}
          helperText={errors.reason}
        />

        <Box>
          <Typography variant="body2" color="text.secondary">
            {TEXTS.estimatedStock}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatStockQuantity(estimatedStock)}
          </Typography>
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel={TEXTS.save}
        saveButtonProps={{ isLoading: isLocked }}
        cancelButtonProps={{ disabled: isLocked }}
      />
    </DialogContainer>
  );
};

export default AdjustArticleStockDialog;
